import { useContext, useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import EcommerceHeader from "../components/EcommerceHeader";
import { CartIcon, HistoryIcon } from "../components/EcommerceIcons";
import { AppContext } from "../context/AppContext";
import { products } from "../data/products";
import { readCart, saveCart } from "../utils/ecommerceStorage";
import "../css/Ecommerce.css";

function isInactiveAccountStatus(accountStatus) {
  if (accountStatus === false || accountStatus === 0) return true;
  if (typeof accountStatus !== "string") return false;
  const normalized = accountStatus.trim().toLowerCase();
  return normalized === "false" || normalized === "0" || normalized === "inactive";
}

function hasInactiveWalletMarker() {
  return localStorage.getItem("inactiveWalletBalance") !== null;
}

export default function EcommerceHome() {
  const navigate = useNavigate();
  const { user, isInactive } = useContext(AppContext);
  const [cartItems, setCartItems] = useState([]);
  const [expandedIds, setExpandedIds] = useState({});

  useEffect(() => {
    const syncCart = () => setCartItems(readCart());
    syncCart();
    window.addEventListener("ecomm-cart-change", syncCart);
    window.addEventListener("storage", syncCart);
    return () => {
      window.removeEventListener("ecomm-cart-change", syncCart);
      window.removeEventListener("storage", syncCart);
    };
  }, []);

  const getQuantity = (productId) => cartItems.find((item) => item.product.id === productId)?.quantity || 0;

  const addToCart = (product) => {
    const nextCart = [...cartItems, { product, quantity: 1 }];
    setCartItems(nextCart);
    saveCart(nextCart);
  };

  const updateQuantity = (productId, quantity) => {
    const nextCart =
      quantity <= 0
        ? cartItems.filter((item) => item.product.id !== productId)
        : cartItems.map((item) => (item.product.id === productId ? { ...item, quantity } : item));

    setCartItems(nextCart);
    saveCart(nextCart);
  };

  const totalCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const isInactiveUser = isInactive === true || isInactiveAccountStatus(user?.accountStatus) || hasInactiveWalletMarker();

  const handleAddFundsClick = () => {
    navigate("/funds/add?simple=1", { state: { inactiveAddFund: isInactiveUser } });
  };

  return (
    <div className="ecomm-app-screen">
      <EcommerceHeader />

      <main className="ecomm-home-scroll">
        <div className="ecomm-promo-container">
          <button onClick={handleAddFundsClick}>Add funds to buy shop</button>
          <button onClick={() => window.open("https://wa.me/918426021912", "_blank", "noopener,noreferrer")}>
            Customer support 24*7: +918426021912
          </button>
        </div>

        <h2 className="ecomm-catalogue-title">Shop Catalogue</h2>

        <div className="ecomm-book-list">
          {products.map((product) => {
            const quantity = getQuantity(product.id);
            const expanded = expandedIds[product.id] || false;

            return (
              <article className="ecomm-book-card" key={product.id}>
                <div className="ecomm-book-main">
                  <div className="ecomm-book-image-wrap">
                    <img src={product.image} alt={product.title} />
                  </div>

                  <div className="ecomm-book-info">
                    <div>
                      <h3>{product.title}</h3>
                      <div className="ecomm-book-price">₹{product.price}</div>
                    </div>
                    <button
                      type="button"
                      className="ecomm-desc-toggle"
                      onClick={() => setExpandedIds((current) => ({ ...current, [product.id]: !expanded }))}
                    >
                      {expanded ? "Hide Description" : "View Description"}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}>
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                  </div>
                </div>

                {expanded && <div className="ecomm-book-desc">{product.description}</div>}

                <div className="ecomm-book-actions">
                  {quantity > 0 ? (
                    <div className="ecomm-qty">
                      <button onClick={() => updateQuantity(product.id, quantity - 1)}>-</button>
                      <span>{quantity}</span>
                      <button onClick={() => updateQuantity(product.id, quantity + 1)}>+</button>
                    </div>
                  ) : (
                    <button className="ecomm-add-cart" onClick={() => addToCart(product)}>
                      Add to Cart
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </main>

      {cartItems.length > 0 && (
        <div className="ecomm-cart-summary">
          <div>
            <span>Total ({totalCount} items)</span>
            <strong>₹{totalPrice}</strong>
          </div>
          <button onClick={() => navigate("/cart")}>
            <CartIcon />
            <span>View Cart</span>
          </button>
        </div>
      )}

      <EcommerceFooter active="home" />
    </div>
  );
}

export function EcommerceFooter({ active }) {
  const navigate = useNavigate();

  return (
    <footer className="ecomm-footer">
      <button className={active === "history" ? "active" : ""} onClick={() => navigate("/order-history")}>
        <HistoryIcon />
        <span>History</span>
      </button>
      <button className={active === "home" ? "active" : ""} onClick={() => navigate("/home")}>
        <span className="ecomm-footer-brand">Gama567APP</span>
      </button>
      <button onClick={() => window.open("https://wa.me/918426021912", "_blank", "noopener,noreferrer")}>
        <FaWhatsapp className="ecomm-whatsapp-icon" />
        <span>Support</span>
      </button>
    </footer>
  );
}
